import { Button, Card, CardBody, Center, Heading, Spinner, Stack, Tag, Text, useDisclosure } from '@chakra-ui/react';
import { Show } from '@pelicargo/ui';
import { getPrettyMarkupConfigurationType } from '@pelicargo/utils';
import { MarkupConfigurationType } from '@prisma/client';
import { useState } from 'react';

import { trpc } from '../../config/trpc';
import { MarkupConfigurationDrawer } from '../Drawers/MarkupConfigurationDrawer';

type Props = {
  accountId: number;
};

type ConfigurationRow = {
  id: number;
  label: string;
  type: MarkupConfigurationType;
  tenant_id: string;
  is_restricted: boolean;
  markupCount: number;
};

const ConfigurationList = ({
  title,
  description,
  rows,
  isLoading,
  onEdit,
  totalCount,
}: {
  title: string;
  description: string;
  rows: ConfigurationRow[];
  isLoading: boolean;
  onEdit: (id: number) => void;
  /** When the query is capped, the unpaginated total so the cut isn't silent */
  totalCount?: number;
}) => (
  <Stack spacing="2">
    <Heading size="sm">{title}</Heading>
    <Text textStyle="bodySmallRegular" color="text.muted">
      {description}
    </Text>
    <Show if={!isLoading && totalCount != null && totalCount > rows.length}>
      <Text textStyle="bodySmallRegular" color="text.muted">
        Showing the first {rows.length} of {totalCount}. Use Markup Configurations to see them all.
      </Text>
    </Show>
    <Show if={isLoading}>
      <Center py="4">
        <Spinner />
      </Center>
    </Show>
    <Show if={!isLoading && !rows.length}>
      <Text textStyle="bodySmallRegular" color="text.muted">
        None
      </Text>
    </Show>
    {rows.map((row) => (
      <Card key={row.id} variant="outline">
        <CardBody py="3">
          <Stack direction="row" align="center" justify="space-between">
            <Stack direction="row" align="center" spacing="3">
              <Text>{row.label || '[Untitled]'}</Text>
              <Tag size="sm" variant="subtle">
                {row.tenant_id}
              </Tag>
              <Tag size="sm" variant="subtle">
                {getPrettyMarkupConfigurationType(row.type)}
              </Tag>
              <Show if={row.is_restricted}>
                <Tag size="sm" colorScheme="red">
                  Restricted
                </Tag>
              </Show>
              <Text textStyle="bodySmallRegular" color="text.muted">
                {row.markupCount} markup{row.markupCount === 1 ? '' : 's'}
              </Text>
            </Stack>
            <Button size="sm" variant="outline" onClick={() => onEdit(row.id)}>
              Edit
            </Button>
          </Stack>
        </CardBody>
      </Card>
    ))}
  </Stack>
);

/**
 * The markup configurations tab on an account. This is a launcher for the shared
 * `MarkupConfigurationDrawer`, not a second editor — see `/markup-configurations`
 * for the full list.
 */
export const FulfillmentPartnerConfigurationsForm = ({ accountId }: Props) => {
  const drawer = useDisclosure();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  // Only pin the account when creating from this tab; editing keeps whatever the config already has
  const [isCreating, setIsCreating] = useState(false);

  const { data: asFulfillmentPartner, isLoading: isLoadingFp } = trpc.getMarkupConfigurationsByAccount.useQuery(
    { accountId },
    { enabled: !!accountId },
  );

  const { data: asRequestor, isLoading: isLoadingRequestor } = trpc.getMarkupConfigurations.useQuery(
    { requestor_account_id: accountId, take: 100 },
    { enabled: !!accountId },
  );

  const handleEdit = (id: number) => {
    setIsCreating(false);
    setSelectedId(id);
    drawer.onOpen();
  };

  const handleCreate = () => {
    setIsCreating(true);
    setSelectedId(null);
    drawer.onOpen();
  };

  const handleClose = () => {
    setSelectedId(null);
    setIsCreating(false);
    drawer.onClose();
  };

  return (
    <>
      <Stack spacing="6">
        <ConfigurationList
          title="As Fulfillment Partner"
          description="Configurations this account owns and gets paid through."
          isLoading={isLoadingFp}
          onEdit={handleEdit}
          rows={(asFulfillmentPartner ?? []).map((config) => ({
            id: config.id,
            label: config.label,
            type: config.type,
            tenant_id: config.tenant_id,
            is_restricted: config.is_restricted,
            markupCount: config.markups.length,
          }))}
        />
        <Button colorScheme="brand" alignSelf="flex-start" onClick={handleCreate}>
          Add Configuration
        </Button>
        <ConfigurationList
          title="As Requestor"
          description="Configurations that list this account under Connected Accounts."
          isLoading={isLoadingRequestor}
          onEdit={handleEdit}
          totalCount={asRequestor?.[0]}
          rows={(asRequestor?.[1] ?? []).map((config) => ({
            id: config.id,
            label: config.label,
            type: config.type,
            tenant_id: config.tenant_id,
            is_restricted: config.is_restricted,
            markupCount: config._count.markups,
          }))}
        />
      </Stack>
      <Show if={drawer.isOpen}>
        <MarkupConfigurationDrawer
          id={selectedId}
          isOpen={drawer.isOpen}
          onClose={handleClose}
          defaultFulfillmentPartnerId={isCreating ? accountId : undefined}
        />
      </Show>
    </>
  );
};
